import Map from 'ol/Map.js';
import { FeatureLike } from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export type MapEventListener = (
  feature: FeatureLike | undefined,
  coordinate: Coordinate,
) => void;

export type MapContext = {
  map: Map | null;
  hoverListeners: React.RefObject<Set<MapEventListener>>;
  clickListeners: React.RefObject<Set<MapEventListener>>;
  cursorStyle: React.RefObject<string | null> | null;
  setCursorStyle: (style: string | null) => void;
  registerHoverListener: (listener: MapEventListener) => void;
  unregisterHoverListener: (listener: MapEventListener) => void;
  registerClickListener: (listener: MapEventListener) => void;
  unregisterClickListener: (listener: MapEventListener) => void;
  setMap: (map: Map) => void;
};

const defaultContext: MapContext = {
  map: null,
  hoverListeners: { current: new Set() },
  clickListeners: { current: new Set() },
  cursorStyle: null,
  setCursorStyle: (_style) => null,
  registerHoverListener: (_listener) => null,
  unregisterHoverListener: (_listener) => null,
  registerClickListener: (_listener) => null,
  unregisterClickListener: (_listener) => null,
  setMap: (_map) => null,
};

export const mapContext = createContext(defaultContext);

export const useMapContextValue = (): MapContext => {
  const hoverListeners = useRef<Set<MapEventListener>>(new Set());
  const clickListeners = useRef<Set<MapEventListener>>(new Set());
  const [map, setMap] = useState<Map | null>(null);
  const cursorStyleRef = useRef<string | null>(null);

  const setCursorStyle = useCallback((style: string | null) => {
    cursorStyleRef.current = style;
  }, []);

  const registerHoverListener = useCallback(
    (listener: MapEventListener) => {
      hoverListeners.current.add(listener);
    },
    [hoverListeners],
  );
  const unregisterHoverListener = useCallback(
    (listener: MapEventListener) => {
      hoverListeners.current.delete(listener);
    },
    [hoverListeners],
  );
  const registerClickListener = useCallback(
    (listener: MapEventListener) => {
      clickListeners.current.add(listener);
    },
    [clickListeners],
  );
  const unregisterClickListener = useCallback(
    (listener: MapEventListener) => {
      clickListeners.current.delete(listener);
    },
    [clickListeners],
  );

  const mapContextValue = useMemo(
    () => ({
      map,
      hoverListeners,
      clickListeners,
      cursorStyle: cursorStyleRef,
      setCursorStyle,
      registerHoverListener,
      unregisterHoverListener,
      registerClickListener,
      unregisterClickListener,
      setMap,
    }),
    [
      map,
      hoverListeners,
      clickListeners,
      cursorStyleRef,
      setCursorStyle,
      registerHoverListener,
      unregisterHoverListener,
      registerClickListener,
      unregisterClickListener,
    ],
  );

  return mapContextValue;
};

export const useMapContext = () => {
  return useContext(mapContext);
};
